import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  MapPin,
  Lock,
  ArrowRight,
  Plus,
  Building2,
  ExternalLink,
  CheckCircle2,
  Eye,
  Info,
  MapPinned,
  CircleOff,
  Layers,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useVendorSubscriptionFeatures } from '../../services/subscriptionService';
import { useToastContext } from '../../contexts/ToastContext';
import {
  useVendorLocations,
  useCreateVendorLocation,
  useUpdateVendorLocation,
  useDeleteVendorLocation,
  useToggleVendorLocationStatus,
} from '../../services/vendorLocationService';
import LocationCard from '../../components/vendor/locations/LocationCard';
import LocationFormModal from '../../components/vendor/locations/LocationFormModal';
import VendorServiceAreas from '../../components/client/VendorServiceAreas';
import Button from '../../components/shared/Button';
import StatsGrid from '../../components/shared/StatsGrid';

const VendorMultiLocation = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { success: showSuccess, error: showError } = useToastContext();
  const { hasActiveSubscription, isChecking, hasFeature, isLoading: featureLoading } =
    useVendorSubscriptionFeatures('feature_multi_location');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [togglingId, setTogglingId] = useState(null);

  const { data: locationsResponse, isLoading: locationsLoading } = useVendorLocations({
    enabled: hasActiveSubscription === true && hasFeature,
  });

  const createMutation = useCreateVendorLocation({
    onSuccess: () => {
      showSuccess('Location added', 'Your new service area is saved.');
      closeModal();
    },
    onError: (err) => {
      showError('Could not add location', err?.response?.data?.message || 'Please try again.');
    },
  });

  const updateMutation = useUpdateVendorLocation({
    onSuccess: () => {
      showSuccess('Location updated', 'Changes saved successfully.');
      closeModal();
    },
    onError: (err) => {
      showError('Could not update location', err?.response?.data?.message || 'Please try again.');
    },
  });

  const deleteMutation = useDeleteVendorLocation({
    onSuccess: () => showSuccess('Location removed', 'Service area deleted.'),
    onError: (err) => {
      showError('Could not remove location', err?.response?.data?.message || 'Please try again.');
    },
  });

  const toggleMutation = useToggleVendorLocationStatus({
    onSuccess: (res) => {
      showSuccess('Status updated', res?.message || 'Location status changed.');
    },
    onError: (err) => {
      showError('Could not update status', err?.response?.data?.message || 'Please try again.');
    },
  });

  useEffect(() => {
    if (user?.user_type !== 'vendor') {
      navigate('/');
      return;
    }
    if (hasActiveSubscription === false) {
      navigate('/vendor/subscription', { replace: true });
    }
  }, [user?.user_type, hasActiveSubscription, navigate]);

  const pageData = locationsResponse?.data || {};
  const locations = pageData.locations || [];
  const primary = pageData.primary_location || {};
  const stats = pageData.statistics || {};
  const maxLocations = pageData.max_locations || 25;
  const canAddMore = (stats.remaining ?? maxLocations) > 0;
  const activeLocations = locations.filter((location) => location.is_active);

  const closeModal = () => {
    setModalOpen(false);
    setEditingLocation(null);
  };

  const openCreate = () => {
    setEditingLocation(null);
    setModalOpen(true);
  };

  const openEdit = (location) => {
    setEditingLocation(location);
    setModalOpen(true);
  };

  const handleSubmit = (payload) => {
    if (editingLocation) {
      updateMutation.mutate({ id: editingLocation.id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const handleDelete = (location) => {
    if (!window.confirm(`Remove "${location.area_name}" from your service areas?`)) return;
    setDeletingId(location.id);
    deleteMutation.mutate(location.id, { onSettled: () => setDeletingId(null) });
  };

  const handleToggle = (location) => {
    setTogglingId(location.id);
    toggleMutation.mutate(location.id, { onSettled: () => setTogglingId(null) });
  };

  const primaryLabel = [primary.city, primary.state, primary.country].filter(Boolean).join(', ');
  const publicProfileUrl = pageData.profile_id ? `/vendor/${pageData.profile_id}` : null;

  const statsCards = [
    {
      key: 'total',
      title: 'Total areas',
      value: stats.total ?? 0,
      icon: Layers,
      variant: 'default',
    },
    {
      key: 'active',
      title: 'Active on profile',
      value: stats.active ?? 0,
      icon: CheckCircle2,
      variant: 'success',
    },
    {
      key: 'inactive',
      title: 'Inactive',
      value: stats.inactive ?? 0,
      icon: CircleOff,
      variant: stats.inactive > 0 ? 'warning' : 'default',
    },
    {
      key: 'remaining',
      title: 'Slots remaining',
      value: stats.remaining ?? maxLocations,
      icon: MapPinned,
      variant: canAddMore ? 'info' : 'warning',
    },
  ];

  if (isChecking || featureLoading || (hasFeature && locationsLoading) || hasActiveSubscription !== true) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (!hasFeature) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg border border-gray-200 p-8 text-center">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-amber-600" />
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Multi-Location</h1>
          <p className="text-gray-600 mb-6">
            This feature is not included in your current plan. Upgrade to manage multiple service locations and reach clients across different areas.
          </p>
          <Link
            to="/vendor/subscription"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors"
          >
            View Plans
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-11 h-11 bg-primary-600 rounded-xl flex items-center justify-center">
                <MapPin className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Multi-Location</h1>
                <p className="text-sm text-gray-600">
                  Show clients everywhere you operate — on your public vendor profile
                </p>
              </div>
            </div>
            {stats.active > 0 && (
              <p className="inline-flex items-center gap-1.5 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1 mt-2">
                <CheckCircle2 className="w-4 h-4" />
                {stats.active} active service {stats.active === 1 ? 'area' : 'areas'} live on your profile
              </p>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {publicProfileUrl && (
              <Link to={publicProfileUrl} target="_blank" rel="noopener noreferrer">
                <Button type="button" variant="outline" size="sm" icon={ExternalLink}>
                  View public page
                </Button>
              </Link>
            )}
            <Button
              type="button"
              size="sm"
              icon={Plus}
              onClick={openCreate}
              disabled={!canAddMore}
            >
              Add location
            </Button>
          </div>
        </div>

        <section className="bg-blue-50 border border-blue-200 rounded-xl p-4 sm:p-5">
          <div className="flex gap-3">
            <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-900 space-y-2">
              <p className="font-medium">How multi-location works</p>
              <ul className="list-disc pl-4 space-y-1 text-blue-800">
                <li>
                  Your <strong>primary business address</strong> comes from your business profile and always represents your HQ.
                </li>
                <li>
                  <strong>Additional service areas</strong> are neighborhoods, cities, or regions you travel to — clients see these under &quot;Service areas&quot; on your public page.
                </li>
                <li>
                  Travel fees and times are optional and shown to clients for planning — they do not change booking totals automatically.
                </li>
              </ul>
            </div>
          </div>
        </section>

        <StatsGrid stats={statsCards} columns={4} />

        <div className="grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-6">
          <div className="space-y-6">
            <section className="bg-white rounded-xl border border-gray-200 p-5 sm:p-6">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-5 h-5 text-gray-700" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <h2 className="text-base font-semibold text-gray-900">Primary business location</h2>
                    <span className="text-[10px] uppercase tracking-wide font-medium px-2 py-0.5 rounded-full bg-violet-100 text-violet-800">
                      HQ
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">
                    This is your registered business address. It is separate from the extra areas you add below.
                  </p>
                  <p className="text-sm font-medium text-gray-800">{primary.business_name || 'Your business'}</p>
                  {primaryLabel && <p className="text-sm text-gray-600 mt-0.5">{primaryLabel}</p>}
                  {primary.address && <p className="text-xs text-gray-500 mt-1">{primary.address}</p>}
                  {primary.service_radius > 0 && (
                    <p className="text-xs text-gray-500 mt-2">
                      Default service radius: {primary.service_radius} miles from HQ
                    </p>
                  )}
                  <Link
                    to="/vendor/account?tab=business"
                    className="inline-block text-xs text-primary-600 hover:text-primary-700 underline mt-2"
                  >
                    Edit in business profile
                  </Link>
                </div>
              </div>
            </section>

            <section>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Additional service areas</h2>
                  <p className="text-sm text-gray-600 mt-0.5">
                    {locations.length === 0
                      ? 'Add areas beyond your HQ so nearby clients know you serve them.'
                      : `${locations.length} of ${maxLocations} locations used`}
                  </p>
                </div>
                {!canAddMore && (
                  <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 rounded-full">
                    Maximum {maxLocations} locations reached
                  </span>
                )}
              </div>

              {locations.length === 0 ? (
                <div className="bg-white rounded-xl border border-dashed border-gray-300 p-10 text-center">
                  <MapPin className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                  <h3 className="text-base font-medium text-gray-900 mb-1">No extra locations yet</h3>
                  <p className="text-sm text-gray-600 max-w-md mx-auto mb-4">
                    Add neighborhoods, cities, or regions you serve beyond your primary address. Only active areas appear on your public profile.
                  </p>
                  <Button type="button" icon={Plus} onClick={openCreate}>
                    Add your first location
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {locations.map((location) => (
                    <LocationCard
                      key={location.id}
                      location={location}
                      onEdit={openEdit}
                      onDelete={handleDelete}
                      onToggle={handleToggle}
                      isToggling={togglingId === location.id}
                      isDeleting={deletingId === location.id}
                    />
                  ))}
                </div>
              )}
            </section>
          </div>

          <aside className="xl:sticky xl:top-24 h-fit">
            <section className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                <div className="flex items-center gap-2">
                  <Eye className="w-4 h-4 text-gray-600" />
                  <h3 className="text-sm font-semibold text-gray-900">Client preview</h3>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  How your service areas appear on your public profile
                </p>
              </div>

              <div className="p-4 space-y-4">
                <div className="rounded-lg border border-gray-200 bg-gray-50/80 p-3">
                  <p className="text-[10px] uppercase tracking-wide text-gray-500 mb-1">Primary location</p>
                  <p className="text-sm font-medium text-gray-900">{primary.business_name || 'Your business'}</p>
                  <p className="text-xs text-gray-600 mt-0.5">{primaryLabel || 'Set your address in business profile'}</p>
                </div>

                <div>
                  <p className="text-sm font-medium text-gray-900 mb-2">Service areas</p>
                  <VendorServiceAreas areas={activeLocations} preview />
                </div>

                {publicProfileUrl && (
                  <Link
                    to={publicProfileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs text-primary-600 hover:text-primary-700 underline"
                  >
                    Open full public profile
                    <ExternalLink className="w-3 h-3" />
                  </Link>
                )}
              </div>
            </section>
          </aside>
        </div>
      </div>

      <LocationFormModal
        isOpen={modalOpen}
        onClose={closeModal}
        onSubmit={handleSubmit}
        initialLocation={editingLocation}
        isSaving={createMutation.isPending || updateMutation.isPending}
        primaryLocation={primary}
      />
    </div>
  );
};

export default VendorMultiLocation;
